import React, { useEffect } from 'react';
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react';

interface ToastProps {
  open: boolean;
  onClose: () => void;
  message: string;
  severity?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

export const Toast: React.FC<ToastProps> = ({
  open,
  onClose,
  message,
  severity = 'info',
  duration = 3000,
}) => {
  useEffect(() => {
    if (open && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [open, duration, onClose]);

  if (!open) return null;

  const icons = {
    success: CheckCircle2,
    error: XCircle,
    warning: AlertCircle,
    info: Info,
  };

  const styles = {
    success: 'bg-success/5 border-success/30 text-success backdrop-blur-md',
    error: 'bg-error/5 border-error/30 text-error backdrop-blur-md',
    warning: 'bg-warning/5 border-warning/30 text-warning backdrop-blur-md',
    info: 'bg-accent/5 border-accent/30 text-accent backdrop-blur-md',
  };

  const Icon = icons[severity];

  return (
    <div className="fixed bottom-4 right-4 left-4 sm:left-auto z-[35] animate-in slide-in-from-bottom-4 fade-in duration-200 max-w-md ml-auto">
      <div
        className={`p-3 md:p-4 w-full flex items-start gap-2 md:gap-3 rounded-lg border shadow-lg bg-background-secondary/90 ${styles[severity]}`}
      >
        <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-foreground">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-black/10 transition-smooth flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
